// ────────────────────────────────────────────────────────────────────
// QUEUE — the staff pipeline as ONE list, split by whose move it is.
// Spec: docs/superpowers/specs/2026-09-01-the-queue-design.md, plan Task 3.
//
// ── One list ────────────────────────────────────────────────────────
// `On you now` and `Moving elsewhere` are two headings over one list. They are
// not two sources, not two fetches, and not two sections that could be filtered
// separately — the rows come back from GET /api/queue already sorted as a
// single set and split on `mine`, and this screen rebuilds that set before it
// does anything (queueModel.allRows) so every figure and every rail on the page
// is a view of the same rows.
//
// That is the entire correction. The screen this replaces stacked three
// sections from three endpoints, and its chips filtered the first one only —
// so the count at the top and the rows underneath described different sets of
// jobs, and no test caught it because each section was individually correct.
// A layout that makes a per-section filter impossible is worth more than a test
// that checks the sections currently agree.
//
// ── The browser composes nothing ────────────────────────────────────
// Every sentence on a row — the stage chip, the evidence line, NEXT ACTION,
// the age, the verb — arrives finished from the server. This screen prints. It
// does not join, phrase, pluralise or infer. Counting the rows a user has
// filtered down to is the one exception and queueModel.ts says why.
//
// ── The six gaps Task 3 reported, and what became of them ───────────
// Each was a thing the design draws that the wire did not carry. None was
// approximated in the browser; five were closed by widening the wire and one
// stands, deliberately.
//
//   1. The six stages numbered 1–6 with their owning role. CLOSED —
//      `counts.by_stage` now carries EVERY stage the queue covers, empty ones
//      included, each with its `index` and `owner_role`. A stage holding
//      nothing is information: it says the pipeline is clear there.
//   2. A count against `A Archive · closed`. CLOSED — `counts.archive_closed`,
//      both outcomes. Archive is excluded from the QUEUE, not from the record.
//   3. Thirty-day throughput. CLOSED — the data was what was wrong, not the
//      label. `counts.throughput_days/_closed/_median_days` is the design's
//      `THROUGHPUT · 30D`, and `closed_this_week` stays a week because the KPI
//      says `CLOSED THIS WK` and one figure cannot be both.
//   4. `FLOW · LAST 24H`. STANDS as `FLOW · IN THE QUEUE NOW`. Nothing records
//      when a job ENTERED a stage, so there is no 24-hour window to read — see
//      QueueRail.tsx for why the newly-written stage events do not make one.
//   5. `ALERTS`. CLOSED, against the real source: GET /api/notifications, the
//      per-user feed `server/notify.py` writes and the header bell already
//      shows. Two honest limits, recorded rather than papered over:
//        · that feed is per-USER, not per-tenant, so an alert about another
//          tenant's job can appear beside a tenant-scoped queue. No new
//          exposure — it is the same list the bell shows — but it is not the
//          queue narrowed, and the rail does not pretend it is.
//        · of the design's three example lines, only the file/job family has
//          a producer today (`topic="submission"`). Nothing in this tree emits
//          a t-min breach or an SLA breach as a notification; SLA breach is on
//          the rows themselves as `overdue`, and t-min has no writer at all.
//   6. A route to fire the verb. CLOSED — `POST /api/submissions/{id}/advance`
//      exists, and this screen fires it. The permission is still entirely the
//      server's: the button renders only where `action` came back, the route
//      asks the same two functions the queue asked, and a refusal is printed
//      in the server's own words rather than guessed at here.
//
// ── Colour ──────────────────────────────────────────────────────────
// `--ink-4` (#a8a29e) renders at 2.28–2.52:1 on this paper and appears nowhere
// in this directory, in any shape — not as a literal, not through a named
// constant. Content is `--ink-3` or darker; accent as text is `--accent-deep`
// and `--accent` stays a fill. Job ids and client names are the two fields you
// read to identify a row, they are what was measured at 2.5:1 on the landing
// screen, and this screen is denser with them than that one was.
// ────────────────────────────────────────────────────────────────────
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiClient, getCachedUser } from "@/lib/api";
import type { AppNotification } from "@/lib/types";
import { QueueRowCard } from "./QueueRowCard";
import { QueueSidebar } from "./QueueSidebar";
import { QueueRail } from "./QueueRail";
import {
  allRows, applyFilter, clientKey, flowStages, queueSummary, toggleFilter,
} from "./queueModel";
import type { QueueFilter } from "./queueModel";
import type { QueueOut, QueueRow } from "./types";

const MICRO = {
  fontFamily: "var(--mono)", fontSize: 10, letterSpacing: "0.09em",
  textTransform: "uppercase" as const, color: "var(--ink-3)",
};

function Kpi(
  { label, value, note, testid }: {
    label: string; value: number | string; note?: string; testid: string;
  },
) {
  return (
    <div
      style={{
        flex: 1, minWidth: 0, padding: "10px 12px",
        border: "1px solid var(--hairline)", borderRadius: 3,
        background: "var(--paper-2)",
      }}
    >
      <div style={MICRO}>{label}</div>
      <div
        data-testid={testid}
        style={{ fontFamily: "var(--mono)", fontSize: 22, color: "var(--ink-1)", marginTop: 2 }}
      >
        {value}
      </div>
      {note && (
        <div style={{ ...MICRO, letterSpacing: "0.06em", textTransform: "none" }}>{note}</div>
      )}
    </div>
  );
}

function Section(
  { id, title, caption, rows, onAdvance, empty }: {
    id: string; title: string; caption: string; rows: QueueRow[];
    onAdvance: (row: QueueRow) => void; empty: string;
  },
) {
  return (
    <section data-testid={`queue-section-${id}`} style={{ marginTop: 20 }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
        <h2 style={{ margin: 0, fontSize: 15, color: "var(--ink-1)", fontWeight: 600 }}>
          {title}
        </h2>
        <span data-testid={`queue-caption-${id}`} style={{ fontSize: 12, color: "var(--ink-3)" }}>
          {caption}
        </span>
      </div>
      <div
        style={{
          marginTop: 8, border: "1px solid var(--hairline)", borderRadius: 3,
          background: "var(--paper-2)", overflow: "hidden",
        }}
      >
        {rows.map((r) => <QueueRowCard key={r.id} row={r} onAdvance={onAdvance} />)}
        {rows.length === 0 && (
          <div style={{ padding: "14px", fontSize: 12, color: "var(--ink-3)" }}>{empty}</div>
        )}
      </div>
    </section>
  );
}

/** The refusal, in the server's own words. An axios error carries the route's
 *  `detail` — the stage gate's sentence — and that is what is shown. The
 *  browser must not translate a 403 into a guess about why. */
function refusal(err: unknown): string {
  const detail = (err as { response?: { data?: { detail?: unknown } } })
    ?.response?.data?.detail;
  return typeof detail === "string" && detail
    ? detail
    : "the job could not be advanced";
}

export default function QueueScreen() {
  const [filter, setFilter] = useState<QueueFilter | null>(null);
  const qc = useQueryClient();
  const q = useQuery<QueueOut>({ queryKey: ["queue"], queryFn: () => apiClient.queue() });
  // The alert feed — a real, separate source (see note 5). Its own query, so
  // its own failure: a rail that drew "nothing to flag" over a failed fetch
  // would report an outage as calm.
  const alertsQ = useQuery<AppNotification[]>({
    queryKey: ["queue-alerts"], queryFn: () => apiClient.notifications(),
  });
  // The verb, fired. `to_stage` is the server's — this screen only reports
  // which button was pressed, and asks for the whole queue back afterwards
  // rather than editing a row in place: the split, the counts and the next
  // verb are all the server's answers and every one of them just changed.
  const advance = useMutation({
    mutationFn: (row: QueueRow) =>
      apiClient.submissionAdvance(row.id, row.action!.to_stage),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["queue"] });
      qc.invalidateQueries({ queryKey: ["queue-alerts"] });
    },
  });
  const identity = useMemo(() => {
    const u = getCachedUser();
    return u ? { name: u.full_name || u.username, role: u.role as string } : null;
  }, []);

  const data = q.data;
  const rows = useMemo(() => (data ? allRows(data) : []), [data]);
  const visible = useMemo(() => applyFilter(rows, filter), [rows, filter]);
  const summary = queueSummary(visible);
  const onYou = visible.filter((r) => r.mine);
  const elsewhere = visible.filter((r) => !r.mine);

  // The date the reader is looking at this on. The browser's own clock is the
  // only source there could be for it, and it is not queue data.
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long", year: "numeric", month: "long", day: "numeric",
  });

  if (q.isLoading) {
    return <div style={{ padding: 24, ...MICRO }} data-testid="queue-loading">loading…</div>;
  }
  if (q.isError || !data) {
    return (
      <div style={{ padding: 24, fontSize: 13, color: "var(--err)" }} data-testid="queue-error">
        the queue could not be loaded
      </div>
    );
  }

  const activeLabel = filter === null ? null
    : filter.kind === "stage"
      ? (data.counts.by_stage.find((s) => s.stage === filter.key)?.label ?? filter.key)
      : (data.counts.by_client.find((c) => clientKey(c.client_id) === filter.key)?.client ?? "—");

  return (
    <div
      data-testid="queue-screen"
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(13rem, 16rem) minmax(0, 1fr) minmax(12rem, 15rem)",
        minHeight: "100%", background: "var(--paper)",
      }}
    >
      <QueueSidebar
        data={data}
        filter={filter}
        identity={identity}
        onFilter={(next) => setFilter((cur) => toggleFilter(cur, next))}
      />

      <main style={{ padding: "18px 20px", minWidth: 0 }}>
        <div data-testid="queue-date" style={MICRO}>{today}</div>
        <h1 style={{ margin: "6px 0 0", fontSize: 22, color: "var(--ink-1)", fontWeight: 600 }}>
          Your queue
        </h1>
        {/* The product claim, not a caption. */}
        <p data-testid="queue-subtitle" style={{ margin: "3px 0 0", fontSize: 13, color: "var(--ink-3)" }}>
          Sorted by what is blocking someone else, not by date
        </p>

        <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
          {/* Counted off `visible` — the rows rendered below this row of
              figures — so a filter can never leave a number standing over a
              list it no longer describes. */}
          <Kpi testid="kpi-on-you" label="On you" value={summary.onYou} />
          <Kpi testid="kpi-in-flight" label="In flight" value={summary.total} />
          <Kpi testid="kpi-breached" label="Breached SLA" value={summary.overdue} />
          <Kpi
            testid="kpi-closed"
            label="Closed this wk"
            value={data.counts.closed_this_week}
            note="completions · off the queue, not filtered"
          />
        </div>

        {filter !== null && (
          <div
            data-testid="queue-active-filter"
            style={{
              marginTop: 12, padding: "6px 10px", borderRadius: 3,
              border: "1px solid var(--accent-deep)", background: "var(--surface-warm-2)",
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <span style={{ ...MICRO, color: "var(--accent-deep)" }}>filtered</span>
            <span style={{ flex: 1, fontSize: 13, color: "var(--ink-2)" }}>{activeLabel}</span>
            <button
              type="button"
              data-testid="queue-clear-filter"
              onClick={() => setFilter(null)}
              style={{
                cursor: "pointer", padding: "3px 9px", borderRadius: 3,
                border: "1px solid var(--hairline)", background: "var(--paper-2)",
                ...MICRO, color: "var(--ink-2)",
              }}
            >
              clear
            </button>
          </div>
        )}

        {advance.isError && (
          // A verb that was refused says WHY, in the server's own words. The
          // row only renders a button where `action` came back, so a refusal
          // here is the gate having changed its mind between the fetch and the
          // click — someone else moved the job, or the caller's authority
          // changed. Guessing at which would be the browser inventing a
          // reason; the route already sent one.
          <div
            data-testid="queue-advance-error"
            style={{
              marginTop: 12, padding: "6px 10px", borderRadius: 3,
              border: "1px solid var(--err)", background: "var(--surface-alert)",
              display: "flex", alignItems: "center", gap: 10,
            }}
          >
            <span style={{ ...MICRO, color: "var(--err)" }}>refused</span>
            <span style={{ flex: 1, fontSize: 13, color: "var(--ink-2)" }}>
              {refusal(advance.error)}
            </span>
            <button
              type="button"
              data-testid="queue-advance-error-dismiss"
              onClick={() => advance.reset()}
              style={{
                cursor: "pointer", padding: "3px 9px", borderRadius: 3,
                border: "1px solid var(--hairline)", background: "var(--paper-2)",
                ...MICRO, color: "var(--ink-2)",
              }}
            >
              dismiss
            </button>
          </div>
        )}

        <Section
          id="on-you"
          title="On you now"
          caption={`${summary.onYou} jobs waiting on ${identity?.role ?? "you"}`}
          rows={onYou}
          onAdvance={(row) => advance.mutate(row)}
          empty="nothing is waiting on you"
        />
        <Section
          id="elsewhere"
          title="Moving elsewhere"
          caption={`${summary.movingElsewhere} jobs held by other roles — visible, not actionable`}
          rows={elsewhere}
          onAdvance={(row) => advance.mutate(row)}
          empty="nothing is moving elsewhere"
        />
      </main>

      <QueueRail
        flow={flowStages(data, visible)}
        blocked={visible.filter((r) => r.blocked)}
        alerts={alertsQ.data ?? []}
        alertsUnavailable={alertsQ.isError}
      />
    </div>
  );
}
